/**
 * UnmatchedCallsView — the queue of Dialpad calls that could not be filed onto a client's case
 * automatically (Task 10b, frontend half of the Dialpad call integration).
 *
 * A call lands here for exactly one of two reasons, and the reasons are NOT interchangeable:
 *   - 'no-client': the number matched no client at all. Staff need to find or create the client.
 *   - 'ambiguous-client': the number is shared by two or more clients, so filing automatically
 *     would risk putting a privileged attorney-client call summary in the wrong person's legal
 *     file. Staff need to choose the correct client from the ones on file.
 * The two banners below are worded differently on purpose — collapsing them into one generic
 * "could not match" message would hide the reason the queue exists at all.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import ManagerApi, { ManagerApiError, UnmatchedCallDto } from '../../api/ManagerApi';
import UserApi from '../../api/UserApi';
import { FullUserDto } from '../../interfaces/UserInterfaces';
import { fetchTicketsPage } from '../../api/fetchTicketsPage';
import type { ContextTicketDto } from '../HomeView/HomeView';

const OUTCOME_LABELS: Record<UnmatchedCallDto['UnmatchedCall_Outcome'], string> = {
  spoke: 'Spoke',
  voicemail: 'Voicemail',
  'no-answer': 'No answer',
};

const DIRECTION_LABELS: Record<UnmatchedCallDto['UnmatchedCall_Direction'], string> = {
  inbound: 'Inbound',
  outbound: 'Outbound',
};

// The entire reason this queue exists instead of the system guessing — worded distinctly per
// reason so the remedy (find/create a client vs. choose among known clients) is obvious.
const REASON_COPY: Record<
  UnmatchedCallDto['UnmatchedCall_Reason'],
  { label: string; description: string; severity: 'warning' | 'error' }
> = {
  'no-client': {
    label: 'No client on file',
    description:
      "This number doesn't match any client in the system. Find the right client, or create a new one, then attach this call to their file.",
    severity: 'warning',
  },
  'ambiguous-client': {
    label: 'Multiple clients matched',
    description:
      "This number is shared by more than one client, so filing it automatically was held back rather than risk putting this privileged call summary in the wrong person's case. Choose the correct client below.",
    severity: 'error',
  },
};

function formatPhoneNumber(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    const d = digits.slice(1);
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return 'Unknown length';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US');
  } catch {
    return iso;
  }
}

function formatClientName(user: FullUserDto): string {
  const middle = user.User_MiddleName ? ` ${user.User_MiddleName}` : '';
  return `${user.User_FirstName}${middle} ${user.User_LastName}`;
}

const UnmatchedCallsView: React.FC = () => {
  const [calls, setCalls] = useState<UnmatchedCallDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listNotice, setListNotice] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [dismissingGuid, setDismissingGuid] = useState<string | null>(null);

  // Attach dialog state
  const [attachTarget, setAttachTarget] = useState<UnmatchedCallDto | null>(null);
  const [clientInputValue, setClientInputValue] = useState('');
  const [clientOptions, setClientOptions] = useState<FullUserDto[]>([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  // Set when a search request itself fails (network/500) — kept distinct from "no matches" so
  // staff don't read a transient failure as "this client doesn't exist" and create a duplicate.
  const [clientSearchError, setClientSearchError] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<FullUserDto | null>(null);
  const [ticketOptions, setTicketOptions] = useState<ContextTicketDto[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState<string | null>(null);
  const [selectedTicketGuid, setSelectedTicketGuid] = useState<string>('');
  const [attachSubmitting, setAttachSubmitting] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ManagerApi.getUnmatchedCalls();
      setCalls(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load unmatched calls');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  // Guards the client search against a stale-response race: MUI's debounce only coalesces
  // invocations inside its 300ms window, not the network responses that come back from calls
  // that already fired. Each debounced invocation stamps a sequence number before issuing its
  // (possibly two, merged) requests; a response is only applied if it's still the latest
  // sequence by the time it resolves.
  const clientSearchSeq = useRef(0);

  // Debounced client search across the whole name — `User_Name` matches any part of it against
  // first, middle or last name, so "Ferrell" and "Surette" both find Ferrell Gene Surette Jr.
  const debouncedClientSearch = useCallback(
    debounce(async (term: string) => {
      const seq = ++clientSearchSeq.current;
      setClientLoading(true);
      setClientSearchError(null);
      try {
        const results = term.trim()
          ? await UserApi.getUsersForManager({ User_Name: term.trim(), to: 20 })
          : await UserApi.getUsersForManager({
              to: 20,
              sortBy: 'User_LastName',
              sortDir: 'asc',
            });

        // A newer search has been issued since this one started — this response is stale.
        if (seq !== clientSearchSeq.current) return;
        setClientOptions(results);
      } catch (err) {
        if (seq !== clientSearchSeq.current) return;
        console.error('Error searching clients:', err);
        setClientOptions([]);
        setClientSearchError(err instanceof Error ? err.message : 'Client search failed');
      } finally {
        if (seq === clientSearchSeq.current) {
          setClientLoading(false);
        }
      }
    }, 300),
    [],
  );

  useEffect(() => {
    if (clientOpen) {
      debouncedClientSearch(clientInputValue);
    }
  }, [clientInputValue, clientOpen, debouncedClientSearch]);

  // Load the selected client's cases so staff can optionally pin the note to a specific one.
  // The `cancelled` flag already prevents a stale response from a previous client selection
  // from landing here (each effect run gets its own closure, and the cleanup below fires
  // before the next run starts), so no separate sequence guard is needed the way client search
  // needed one.
  useEffect(() => {
    if (!selectedClient) {
      setTicketOptions([]);
      setSelectedTicketGuid('');
      setTicketsError(null);
      return;
    }

    let cancelled = false;
    setTicketsLoading(true);
    setTicketsError(null);
    fetchTicketsPage({ userGuid: selectedClient.User_guid, size: 50 })
      .then(({ tickets }) => {
        if (!cancelled) setTicketOptions(tickets);
      })
      .catch((err) => {
        console.error('Error loading client cases:', err);
        if (!cancelled) {
          setTicketOptions([]);
          // Distinguish "the fetch failed" from "this client genuinely has no cases" — the
          // helper text below reads very differently depending on which one happened.
          setTicketsError(err instanceof Error ? err.message : 'Failed to load cases');
        }
      })
      .finally(() => {
        if (!cancelled) setTicketsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedClient]);

  const openAttachDialog = (call: UnmatchedCallDto) => {
    setAttachTarget(call);
    setClientInputValue('');
    setClientOptions([]);
    setClientSearchError(null);
    setSelectedClient(null);
    setTicketOptions([]);
    setTicketsError(null);
    setSelectedTicketGuid('');
    setAttachError(null);
  };

  const closeAttachDialog = () => {
    if (attachSubmitting) return;
    setAttachTarget(null);
  };

  // A 409 means a colleague already resolved or dismissed this row while the screen was open —
  // not a generic failure. Tell the user plainly and refresh the list, rather than a raw error.
  const handleAlreadyHandled = () => {
    setAttachTarget(null);
    setListNotice('Someone else already handled that call — the list below has been refreshed.');
    fetchCalls();
  };

  const handleAttachSubmit = async () => {
    if (!attachTarget || !selectedClient) return;

    setAttachSubmitting(true);
    setAttachError(null);
    try {
      await ManagerApi.attachUnmatchedCall({
        UnmatchedCall_guid: attachTarget.UnmatchedCall_guid,
        User_guid: selectedClient.User_guid,
        ...(selectedTicketGuid ? { Ticket_guid: selectedTicketGuid } : {}),
      });

      // Update local state instead of refetching — no need to round-trip GET /list again.
      setCalls((current) =>
        current.filter((c) => c.UnmatchedCall_guid !== attachTarget.UnmatchedCall_guid),
      );
      setSuccessMessage(`Call attached to ${formatClientName(selectedClient)}.`);
      setAttachTarget(null);
    } catch (err) {
      if (err instanceof ManagerApiError && err.status === 409) {
        handleAlreadyHandled();
      } else {
        setAttachError(err instanceof Error ? err.message : 'Failed to attach call');
      }
    } finally {
      setAttachSubmitting(false);
    }
  };

  const handleDismiss = async (call: UnmatchedCallDto) => {
    const confirmed = window.confirm(
      'Dismiss this call? It will be removed from the unmatched queue without filing a note on any client.',
    );
    if (!confirmed) return;

    setDismissingGuid(call.UnmatchedCall_guid);
    setError(null);
    try {
      await ManagerApi.dismissUnmatchedCall(call.UnmatchedCall_guid);
      setCalls((current) => current.filter((c) => c.UnmatchedCall_guid !== call.UnmatchedCall_guid));
    } catch (err) {
      if (err instanceof ManagerApiError && err.status === 409) {
        setListNotice('Someone else already handled that call — the list below has been refreshed.');
        fetchCalls();
      } else {
        setError(err instanceof Error ? err.message : 'Failed to dismiss call');
      }
    } finally {
      setDismissingGuid(null);
    }
  };

  const selectedTicket = useMemo(
    () => ticketOptions.find((ct) => ct.ticket.Ticket_guid === selectedTicketGuid) || null,
    [ticketOptions, selectedTicketGuid],
  );

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Unmatched Calls
          </Typography>
          <Button variant="outlined" onClick={() => fetchCalls()} disabled={loading}>
            Refresh
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Calls that Dialpad could not file onto a client's case automatically. Clear each one by
          attaching it to the right client (and case, if you know it) or dismissing it.
        </Typography>

        {listNotice && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setListNotice(null)}>
            {listNotice}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : calls.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No unmatched calls right now.
              </Typography>
            </Box>
          ) : (
            calls.map((call, index) => {
              const reason = REASON_COPY[call.UnmatchedCall_Reason];
              return (
                <Box key={call.UnmatchedCall_guid}>
                  {index > 0 && <Divider sx={{ my: 2 }} />}
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    sx={{ alignItems: { md: 'flex-start' } }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {formatPhoneNumber(call.UnmatchedCall_ExternalNumber)}
                        </Typography>
                        <Chip label={DIRECTION_LABELS[call.UnmatchedCall_Direction]} size="small" />
                        <Chip label={OUTCOME_LABELS[call.UnmatchedCall_Outcome]} size="small" variant="outlined" />
                      </Stack>

                      <Typography variant="body2" color="text.secondary">
                        {formatDateTime(call.UnmatchedCall_StartedAt)} &middot;{' '}
                        {formatDuration(call.UnmatchedCall_DurationSeconds)}
                      </Typography>

                      <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                        {call.UnmatchedCall_Summary || 'No call summary available.'}
                      </Typography>

                      <Alert severity={reason.severity} sx={{ mt: 1.5 }} icon={false}>
                        <strong>{reason.label}.</strong> {reason.description}
                      </Alert>
                    </Box>

                    <Stack direction={{ xs: 'row', md: 'column' }} spacing={1}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => openAttachDialog(call)}
                      >
                        Attach to client
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        disabled={dismissingGuid === call.UnmatchedCall_guid}
                        onClick={() => handleDismiss(call)}
                      >
                        {dismissingGuid === call.UnmatchedCall_guid ? 'Dismissing…' : 'Dismiss'}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              );
            })
          )}
        </Paper>
      </Container>

      <Dialog open={attachTarget !== null} onClose={closeAttachDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Attach call to a client</DialogTitle>
        <DialogContent>
          {attachTarget && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {formatPhoneNumber(attachTarget.UnmatchedCall_ExternalNumber)} &middot;{' '}
              {formatDateTime(attachTarget.UnmatchedCall_StartedAt)}
            </Typography>
          )}

          {attachError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAttachError(null)}>
              {attachError}
            </Alert>
          )}

          {clientSearchError && (
            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setClientSearchError(null)}>
              Couldn't search for clients: {clientSearchError}. Try again.
            </Alert>
          )}

          <Autocomplete
            open={clientOpen}
            onOpen={() => setClientOpen(true)}
            onClose={() => setClientOpen(false)}
            value={selectedClient}
            onChange={(_event, newValue) => setSelectedClient(newValue)}
            inputValue={clientInputValue}
            onInputChange={(_event, newInputValue) => setClientInputValue(newInputValue)}
            options={clientOptions}
            getOptionLabel={(option) => formatClientName(option)}
            isOptionEqualToValue={(option, value) => option.User_guid === value?.User_guid}
            filterOptions={(opts) => opts} // server-side search — don't re-filter client side
            loading={clientLoading}
            renderOption={(props: any, option: FullUserDto) => (
              <Box component="li" {...props} key={option.User_guid}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body1">{formatClientName(option)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {option.User_Email || 'No email on file'}
                    {option.User_PhoneNumber ? ` · ${formatPhoneNumber(option.User_PhoneNumber)}` : ''}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client"
                placeholder="Search by first or last name..."
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {clientLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              clientSearchError
                ? 'Search failed — try again'
                : clientInputValue
                  ? 'No clients found'
                  : 'Start typing to search for a client'
            }
            loadingText="Searching clients..."
          />

          <TextField
            select
            fullWidth
            sx={{ mt: 2 }}
            label="Case (optional)"
            value={selectedTicketGuid}
            onChange={(e) => setSelectedTicketGuid(e.target.value)}
            disabled={!selectedClient || ticketsLoading}
            helperText={
              !selectedClient
                ? 'Choose a client first'
                : ticketsLoading
                  ? 'Loading cases…'
                  : ticketsError
                    ? `Couldn't load this client's cases: ${ticketsError}. Try reselecting the client.`
                    : ticketOptions.length === 0
                      ? 'This client has no cases on file — the note will be filed without one'
                      : 'Leave blank to file the note without pinning it to a specific case'
            }
          >
            <MenuItem value="">No specific case</MenuItem>
            {ticketOptions.map((ct) => (
              <MenuItem key={ct.ticket.Ticket_guid} value={ct.ticket.Ticket_guid}>
                {ct.ticket.Ticket_Number || ct.ticket.Ticket_Citation || ct.ticket.Ticket_guid}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAttachDialog} disabled={attachSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAttachSubmit}
            disabled={!selectedClient || attachSubmitting}
          >
            {attachSubmitting ? 'Attaching…' : 'Attach'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UnmatchedCallsView;
